/**
 * Stackbar Fixed Radius - Test version with improved animations and corner radius
 *
 * Fixes:
 * 1. Corner radius is limited to half the bar height (prevents overflow on small bars)
 * 2. Smooth transitions when data changes:
 *    - First render with data: animate from bottom (initial) to target
 *    - Subsequent data changes: animate smoothly from current position to new target
 *    - No more "reset to zero" when data updates
 * 3. Horizontal spacing controlled by scaleBand padding
 */
import { Haptics, ImpactStyle } from '@capacitor/haptics';
import {
  formatToMaxFourDigits,
  getNumberFormatLocale,
  IconWrapper,
} from '@flexo/general';
import { AxisBottom, AxisLeft } from '@visx/axis';
import { localPoint } from '@visx/event';
import { Group } from '@visx/group';
import { scaleBand, scaleLinear, scaleOrdinal } from '@visx/scale';
import { BarRounded, BarStack } from '@visx/shape';
import { DateTime } from 'luxon';
import React, {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { animated, useSpring } from 'react-spring';
import { formatDate } from '../helpers';
import styles from './stackbar-graph.module.scss';

const ENV = (import.meta as any).env;

// Configuration for bar styling
const BAR_CONFIG = {
  // Base corner radius (will be limited by bar height)
  cornerRadius: 4,
  // Vertical gap between stacked bars (in pixels)
  stackGap: 1.5,
  // Fixed horizontal gap between bars in pixels (will be converted to paddingInner ratio)
  fixedHorizontalGapPx: 2,
};

const CHART_CONFIG = {
  web: {
    defaultHeight: 350,
    leftMargin: 32,
    containerPadding: 5,
    headerHeight: 48,
  },
  mobile: {
    defaultHeight: 300,
    leftMargin: 38,
    containerPadding: 8,
    headerHeight: 48,
  },
  chartPadding: {
    bottom: 16,
    top: 30,
    yAxisBuffer: 8,
    groupOffset: 2,
  },
  interaction: {
    minVibrationInterval: 50,
  },
  axis: {
    bottomTopOffset: 16,
    leftTopOffset: -10,
  },
};

interface GraphKeyConfig {
  key: string;
  color: string;
  combined: boolean;
  label?: string;
}

export interface StackbarFixedRadiusProps {
  _data: Array<any>;
  graphKeys: GraphKeyConfig[];
  title: string;
  unit: string;
  subtype: string;
  gradientBorder?: boolean;
  height?: number;
  margin?: { top: number; right: number; bottom: number; left: number };
  isLoading?: boolean;
  isLoadingError?: boolean;
  originalData?: any;
  totalValue?: any;
}

const isWeb = ENV?.VITE_APP_IS_WEB === 'true';
const platformConfig = isWeb ? CHART_CONFIG.web : CHART_CONFIG.mobile;

const defaultMargin = {
  top: CHART_CONFIG.chartPadding.top,
  right: 0,
  bottom: CHART_CONFIG.chartPadding.bottom,
  left: platformConfig.leftMargin,
};

function getGreyScaledColor(color: string) {
  let _color = color;
  if (color?.includes('#')) {
    try {
      const red = parseInt(color.substr(1, 2), 16);
      const green = parseInt(color.substr(3, 2), 16);
      const blue = parseInt(color.substr(5, 2), 16);
      const grayscale = Math.round(0.299 * red + 0.587 * green + 0.114 * blue);
      const grayscaleHex = grayscale.toString(16).padStart(2, '0');
      _color = `#${grayscaleHex}${grayscaleHex}${grayscaleHex}60`;
    } catch {
      return color;
    }
  }
  return _color;
}

const AnimatedVisxBarRounded = animated(BarRounded);

interface AnimatedBarProps {
  xPos: number;
  initialY: number;
  initialHeight: number;
  targetY: number;
  targetHeight: number;
  width: number;
  fill: string;
  stroke: string;
  strokeWidth: number;
  radius: number;
  radiusTopLeft: boolean;
  radiusTopRight: boolean;
  radiusBottomLeft: boolean;
  radiusBottomRight: boolean;
  shouldAnimate: boolean;
  delay?: number;
  springConfig?: { tension?: number; friction?: number; mass?: number };
  isCombined?: boolean;
  barXForCombined?: number;
  barWidthForCombined?: number;
}

const AnimatedBar: React.FC<AnimatedBarProps> = ({
  xPos,
  initialY,
  initialHeight,
  targetY,
  targetHeight,
  width,
  fill,
  stroke,
  strokeWidth,
  radius,
  radiusTopLeft,
  radiusTopRight,
  radiusBottomLeft,
  radiusBottomRight,
  shouldAnimate,
  delay = 0,
  springConfig = { tension: 170, friction: 26 },
  isCombined,
  barXForCombined,
  barWidthForCombined,
}) => {
  // FIX: Limit radius to half the bar height to prevent overflow
  const safeRadius = Math.min(radius, targetHeight / 2, width / 2);

  // Track if this is the first render with valid data
  // First render: animate from initial (bottom) to target
  // Subsequent renders: animate smoothly from current position to new target
  const hasAnimatedOnce = useRef(false);
  const isFirstValidRender = !hasAnimatedOnce.current && targetHeight > 0;

  useEffect(() => {
    if (targetHeight > 0 && !hasAnimatedOnce.current) {
      hasAnimatedOnce.current = true;
    }
  }, [targetHeight]);

  const springProps = useSpring({
    // Only use 'from' on first render - subsequent changes animate from current position
    from: isFirstValidRender
      ? { y: initialY, height: initialHeight }
      : undefined,
    to: shouldAnimate
      ? { y: targetY, height: targetHeight }
      : { y: targetY, height: targetHeight, immediate: true },
    config: springConfig,
    delay: isFirstValidRender ? delay : 0, // Only delay on first animation
  });

  const actualXForBarRounded = xPos;

  if (isCombined) {
    const originalBarWidth = barWidthForCombined ?? 0;

    if (originalBarWidth === 1) {
      return (
        <AnimatedVisxBarRounded
          x={barXForCombined ?? 0}
          y={springProps.y}
          height={springProps.height}
          width={1}
          fill={fill}
          stroke={fill}
          strokeWidth={0}
          radius={safeRadius}
          topLeft={radiusTopLeft}
          topRight={radiusTopRight}
          bottomLeft={radiusBottomLeft}
          bottomRight={radiusBottomRight}
        />
      );
    } else if (originalBarWidth < 1) {
      return null;
    }

    const drawSideLines = targetHeight > 0;

    return (
      <g>
        {drawSideLines && (
          <>
            <animated.rect
              x={(barXForCombined ?? 0) - 0.5}
              y={springProps.y}
              width={1}
              height={springProps.height}
              fill="white"
            />
            <animated.rect
              x={(barXForCombined ?? 0) + originalBarWidth - 0.5}
              y={springProps.y}
              width={1}
              height={springProps.height}
              fill="white"
            />
          </>
        )}
        <AnimatedVisxBarRounded
          x={actualXForBarRounded}
          y={springProps.y}
          height={springProps.height}
          width={width}
          fill={fill}
          stroke={stroke}
          strokeWidth={strokeWidth}
          radius={safeRadius}
          topLeft={radiusTopLeft}
          topRight={radiusTopRight}
          bottomLeft={radiusBottomLeft}
          bottomRight={radiusBottomRight}
        />
      </g>
    );
  }

  return (
    <AnimatedVisxBarRounded
      x={actualXForBarRounded}
      y={springProps.y}
      height={springProps.height}
      width={width}
      fill={fill}
      stroke={stroke}
      strokeWidth={strokeWidth}
      radius={safeRadius}
      topLeft={radiusTopLeft}
      topRight={radiusTopRight}
      bottomLeft={radiusBottomLeft}
      bottomRight={radiusBottomRight}
    />
  );
};

export const StackbarFixedRadius: React.FC<StackbarFixedRadiusProps> = ({
  _data = [],
  graphKeys = [],
  title,
  unit,
  subtype,
  height = isWeb
    ? CHART_CONFIG.web.defaultHeight
    : CHART_CONFIG.mobile.defaultHeight,
  margin = defaultMargin,
  isLoading = false,
  isLoadingError = false,
  originalData,
  totalValue,
}) => {
  const { t } = useTranslation();
  const ThemeStore = useSelector((state: any) => state.theme);
  const calendar = useSelector((state: any) => state.calendar);
  const { selectedTimeSet } = calendar.hiveCalendar;
  const chartRef = useRef<HTMLDivElement>(null);
  const svgRef = useRef<SVGSVGElement | null>(null);
  const location = useLocation();
  const pathSegment = location.pathname.split('/')[1];
  let kpiContext = 'Energy';
  if (pathSegment?.includes('economics')) {
    kpiContext = 'Economics';
  }
  const [chartWidth, setChartWidth] = useState(() => {
    return typeof window !== 'undefined'
      ? window.innerWidth - platformConfig.containerPadding * 2
      : 800;
  });

  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [visibleKeys, setVisibleKeys] = useState<Record<string, boolean>>(() =>
    graphKeys.reduce((acc, gk) => {
      acc[gk.key] = true;
      return acc;
    }, {} as Record<string, boolean>)
  );
  const [isVibrating, setIsVibrating] = useState(false);

  const [animate, setAnimate] = useState(false);
  const prevIsLoadingRef = useRef(isLoading);
  const animationTimeoutRef = useRef<number | null>(null);

  const lastHoveredDateRef = useRef<string | null>(null);
  const rafIdRef = useRef<number | null>(null);
  const lastVibrationTimeRef = useRef(0);

  const triggerThrottledHaptic = () => {
    if (isWeb) return;
    const now = Date.now();
    if (
      now - lastVibrationTimeRef.current >=
      CHART_CONFIG.interaction.minVibrationInterval
    ) {
      lastVibrationTimeRef.current = now;
      Haptics.impact({ style: ImpactStyle.Light }).catch((err) => {
        console.warn('Haptics.impact() error:', err);
      });
    }
  };

  const getDate = (d: any) => d.date;

  const processedData = useMemo(() => {
    const filteredKeys = graphKeys.filter((gk) => gk.combined);
    return (_data || []).map((d) => {
      const newEntry: any = { date: getDate(d) };
      graphKeys.forEach((gk) => {
        const complexKey = `widgets.${subtype}.${gk.key}`;
        let value = d[complexKey];
        if (!visibleKeys[gk.key] || value == null || value < 0) {
          value = 0;
        } else if (gk.combined && (gk as any).base) {
          value =
            d[complexKey] - d[`widgets.${subtype}.${filteredKeys[0].key}`];
        }
        newEntry[gk.key] = value;
      });
      return newEntry;
    });
  }, [_data, graphKeys, visibleKeys, subtype]);

  const seriesKeys = useMemo(() => graphKeys.map((gk) => gk.key), [graphKeys]);
  const seriesKeyConfigs = useMemo(() => {
    const map = new Map<string, GraphKeyConfig>();
    graphKeys.forEach((gk) => map.set(gk.key, gk));
    return map;
  }, [graphKeys]);

  const xMax = Math.max(
    0,
    chartWidth - margin.left - margin.right - platformConfig.containerPadding
  );
  const yMax = Math.max(
    0,
    height -
      margin.top -
      margin.bottom -
      platformConfig.headerHeight -
      CHART_CONFIG.chartPadding.yAxisBuffer
  );

  // Calculate paddingInner dynamically to maintain fixed pixel gap between bars
  const numBars = processedData.length || 1;
  const totalGapSpace = BAR_CONFIG.fixedHorizontalGapPx * (numBars - 1);
  const availableBarSpace = xMax - totalGapSpace;
  const barWidth = availableBarSpace / numBars;

  // paddingInner is ratio of gap to (gap + barWidth)
  // For scaleBand: step = barWidth + gap, so paddingInner = gap / step
  const step = barWidth + BAR_CONFIG.fixedHorizontalGapPx;
  const dynamicPaddingInner = step > 0 ? BAR_CONFIG.fixedHorizontalGapPx / step : 0;

  // Small left offset for visual balance
  const leftOffset = BAR_CONFIG.fixedHorizontalGapPx * 0.5;

  const dateScale = useMemo(
    () =>
      scaleBand<string>({
        range: [leftOffset, xMax], // Start with offset on left, align to edge on right
        domain: processedData.map(getDate),
        paddingInner: dynamicPaddingInner, // Fixed pixel gap between bars
        paddingOuter: 0, // No outer padding - right edge aligns with SVG border
      }),
    [xMax, processedData, leftOffset, dynamicPaddingInner]
  );

  const yScale = useMemo(() => {
    const allPositiveStackValues = processedData.map((d) =>
      seriesKeys.reduce((sum, key) => sum + Math.max(0, d[key] || 0), 0)
    );
    const maxPositiveStackValue = Math.max(...allPositiveStackValues, 0);
    return scaleLinear<number>({
      range: [yMax, 0],
      domain: [0, maxPositiveStackValue > 0 ? maxPositiveStackValue : 1],
      nice: true,
    });
  }, [yMax, processedData, seriesKeys]);

  const colorScale = useMemo(
    () =>
      scaleOrdinal<string, string>({
        domain: seriesKeys,
        range: graphKeys.map((gk) => ThemeStore.colors[gk.color] || '#8884d8'),
      }),
    [seriesKeys, graphKeys, ThemeStore.colors]
  );

  const isNullData = useMemo(() => {
    if (!isLoading && _data.length === 0) return true;
    if (
      !isLoading &&
      _data.every((d) =>
        seriesKeys.every((key) => {
          const complexKey = `widgets.${subtype}.${key}`;
          return d[complexKey] === null || d[complexKey] === undefined;
        })
      )
    )
      return true;
    return false;
  }, [isLoading, _data, seriesKeys, subtype]);

  const isInherentlyZeroData = useMemo(() => {
    if (isNullData || isLoading) return false;
    if (_data.length === 0) return false;
    return _data.every((d) =>
      seriesKeys.every((key) => {
        const complexKey = `widgets.${subtype}.${key}`;
        const value = d[complexKey];
        return value === 0 || value === null || value === undefined;
      })
    );
  }, [isLoading, _data, seriesKeys, isNullData, subtype]);

  const is0ValueData = useMemo(() => {
    if (isNullData || isLoading) return false;
    if (processedData.length === 0) return false;
    return processedData.every((d) =>
      seriesKeys.every(
        (key) => d[key] === 0 || d[key] === null || d[key] === undefined
      )
    );
  }, [isLoading, processedData, seriesKeys, isNullData]);

  const handlePointerLeave = () => {
    if (isNullData || is0ValueData) return;
    if (rafIdRef.current !== null) {
      cancelAnimationFrame(rafIdRef.current);
      rafIdRef.current = null;
    }
    lastHoveredDateRef.current = null;
    setSelectedDate(null);
  };

  const handlePointerMove = (
    event: React.MouseEvent<SVGSVGElement> | React.TouchEvent<SVGSVGElement>
  ) => {
    if (isNullData || is0ValueData) return;
    if (!svgRef.current) return;

    if (rafIdRef.current !== null) {
      cancelAnimationFrame(rafIdRef.current);
    }
    rafIdRef.current = requestAnimationFrame(() => {
      const point = localPoint(svgRef.current!, event);
      if (!point) return;

      const { x } = point;
      const pointerX = x - margin.left;
      const bandwidth = dateScale.bandwidth();

      let newHoverDate: string | null = null;
      let closestDist = Infinity;

      processedData.forEach((d) => {
        const dateX = dateScale(getDate(d));
        if (dateX !== undefined) {
          const centerX = dateX + bandwidth / 2;
          const dist = Math.abs(pointerX - centerX);
          if (
            pointerX >= dateX &&
            pointerX <= dateX + bandwidth &&
            dist < closestDist
          ) {
            closestDist = dist;
            newHoverDate = getDate(d);
          }
        }
      });

      if (
        newHoverDate !== null &&
        newHoverDate !== lastHoveredDateRef.current
      ) {
        lastHoveredDateRef.current = newHoverDate;
        setSelectedDate(newHoverDate);
        if (isVibrating === false) {
          setIsVibrating(true);
          Haptics.vibrate({ duration: 10 }).catch(() => {/* haptics unsupported on web */});
          setTimeout(() => setIsVibrating(false), 500);
        }
        if (event.type.startsWith('touch')) {
          triggerThrottledHaptic();
        }
      }
    });
  };

  const cleanupTouchInteraction = () => {
    if (isNullData || is0ValueData) return;
    if (rafIdRef.current !== null) {
      cancelAnimationFrame(rafIdRef.current);
      rafIdRef.current = null;
    }
    lastHoveredDateRef.current = null;
    setSelectedDate(null);
  };

  const getGraphKeyLabel = (key: string) => {
    return t(`widgets.${subtype}.${key}`);
  };

  const selectedBarData = useMemo(() => {
    if (!selectedDate) return null;
    const sourceArray = Array.isArray(originalData)
      ? originalData
      : Array.isArray(_data)
      ? _data
      : [];
    return sourceArray.find((d) => getDate(d) === selectedDate) || null;
  }, [selectedDate, _data, originalData]);

  const legendItems = useMemo(() => {
    const items: Array<{
      type: 'single' | 'group';
      id: string;
      label?: string;
      colorKey?: string;
      config?: GraphKeyConfig;
      memberConfigs?: GraphKeyConfig[];
    }> = [];
    const nonCombined: GraphKeyConfig[] = [];
    const combinedMembers: GraphKeyConfig[] = [];

    graphKeys.forEach((gk) => {
      if (gk.combined) {
        combinedMembers.push(gk);
      } else {
        nonCombined.push(gk);
      }
    });

    nonCombined.forEach((gk) => {
      items.push({
        type: 'single',
        id: gk.key,
        label: getGraphKeyLabel(gk.key),
        colorKey: gk.key,
        config: gk,
      });
    });

    if (combinedMembers.length > 0) {
      items.push({
        type: 'group',
        id: '__combined_group__',
        memberConfigs: combinedMembers,
      });
    }
    return items;
  }, [graphKeys, getGraphKeyLabel, t, subtype]);

  const toggleKeyVisibility = (keyToToggle: string) => {
    if (isInherentlyZeroData || isNullData) return;
    setVisibleKeys((prev) => {
      const willBeVisible = !prev[keyToToggle];
      const currentlyVisibleKeys = seriesKeys.filter((k) => prev[k]).length;

      if (!willBeVisible && currentlyVisibleKeys === 1 && prev[keyToToggle]) {
        return prev;
      }

      return {
        ...prev,
        [keyToToggle]: willBeVisible,
      };
    });
  };

  useEffect(() => {
    if (!isLoading && _data.length > 0 && !animate) {
      setAnimate(true);
    }
  }, [isLoading, _data]);

  useLayoutEffect(() => {
    const updateWidth = () => {
      if (chartRef.current) {
        const containerWidth =
          chartRef.current.offsetWidth ||
          chartRef.current.parentElement?.offsetWidth ||
          window.innerWidth - platformConfig.containerPadding * 2;
        setChartWidth(Math.max(containerWidth, 200));
      }
    };

    updateWidth();
    const resizeObserver = new ResizeObserver(updateWidth);
    if (chartRef.current) {
      resizeObserver.observe(chartRef.current);
    }

    return () => resizeObserver.disconnect();
  }, []);

  useEffect(() => {
    if (chartRef.current) {
      const containerWidth =
        chartRef.current.offsetWidth ||
        chartRef.current.parentElement?.offsetWidth ||
        window.innerWidth - platformConfig.containerPadding * 2;
      setChartWidth(Math.max(containerWidth, 200));
    }
  }, [_data]);

  useEffect(() => {
    setVisibleKeys(
      graphKeys.reduce((acc, gk) => {
        acc[gk.key] = true;
        return acc;
      }, {} as Record<string, boolean>)
    );
  }, [graphKeys]);

  useEffect(() => {
    return () => {
      if (rafIdRef.current !== null) {
        cancelAnimationFrame(rafIdRef.current);
        rafIdRef.current = null;
      }
      if (animationTimeoutRef.current) {
        window.clearTimeout(animationTimeoutRef.current);
        animationTimeoutRef.current = null;
      }
    };
  }, []);

  if (xMax <= 0 || (yMax <= 0 && chartWidth === 0)) {
    return <div ref={chartRef} style={{ width: '100%', height }} />;
  }

  const axisLineColor = ThemeStore.colors.ui_light || '#F7F7F7';
  const axisTickLabelColor = ThemeStore.colors.text_subdued || '#838B93';

  const generateTickValues = (xMin: number, xMax: number): number[] => {
    if (xMin === xMax) return [xMin];
    const numTicks = 4;
    const step = (xMax - xMin) / (numTicks - 1);
    if (numTicks <= 1 || numTicks - 1 === 0) return [xMin];
    return Array.from({ length: numTicks }, (_, i) => {
      const tickTime = xMin + i * step;
      const date = new Date(tickTime);
      date.setMinutes(0, 0, 0);
      return date.getTime();
    });
  };

  let finalTickValues: string[] | undefined = undefined;
  const dateScaleDomain = dateScale.domain();

  if (dateScaleDomain.length > 0) {
    const domainTimestamps = dateScaleDomain.map((d) => new Date(d).getTime());
    const actualXMin = Math.min(...domainTimestamps);
    const actualXMax = Math.max(...domainTimestamps);

    if (isFinite(actualXMin) && isFinite(actualXMax)) {
      const idealTimestamps = generateTickValues(actualXMin, actualXMax);
      const uniqueIdealTimestamps = [...new Set(idealTimestamps)];

      finalTickValues = uniqueIdealTimestamps
        .map((idealTs) => {
          let closestDate = dateScaleDomain[0];
          let minDiff = Math.abs(idealTs - domainTimestamps[0]);

          for (let i = 0; i < dateScaleDomain.length; i++) {
            const diff = Math.abs(idealTs - domainTimestamps[i]);
            if (diff < minDiff) {
              minDiff = diff;
              closestDate = dateScaleDomain[i];
            }
          }
          return closestDate;
        })
        .filter((value, index, self) => self.indexOf(value) === index);
    }
  }

  const getTotalValue = () => {
    const total = totalValue?.find(
      (t: any) => t.date === selectedDate
    )?.totalValue;
    return total ? total + unit : undefined;
  };

  return (
    <div
      ref={chartRef}
      className={
        isWeb ? styles.Stackbar__Wrapper__Web : styles.Stackbar__Wrapper
      }
    >
      <div className={styles.Stackbar__Header}>
        <div className={styles.GraphHeader__TitleContainer}>
          {selectedDate && (
            <div
              className="heading4"
              style={{
                color: ThemeStore.colors.text_primary,
                marginRight: '.5em',
              }}
            >
              {formatDate(selectedDate, selectedTimeSet)}
            </div>
          )}
          <div
            className={`${styles.GraphHeader__Title} ${
              selectedDate ? 'heading4L' : 'heading4'
            }`}
            style={{ color: ThemeStore.colors.text_primary }}
          >
            {t(title) + ' '}
            {selectedBarData && getTotalValue()}
          </div>
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem' }}>
          {legendItems.map((item) => {
            if (
              item.type === 'single' &&
              item.config &&
              item.label &&
              item.colorKey
            ) {
              const gk = item.config;
              return (
                <button
                  key={item.id}
                  onClick={() => toggleKeyVisibility(gk.key)}
                  className={`detail ${styles.Stackbar__Key}`}
                  style={{
                    padding: '0',
                    border: 'none',
                    borderBottom: `4px solid ${
                      visibleKeys[gk.key] ? colorScale(gk.key) : axisLineColor
                    }`,
                    backgroundColor: 'transparent',
                    color: ThemeStore.colors.text_secondary || '#505558',
                    borderRadius: '1px',
                    opacity:
                      selectedDate &&
                      !visibleKeys[gk.key] &&
                      Object.values(visibleKeys).some((v) => !v)
                        ? 0.5
                        : 1,
                    cursor:
                      isNullData || isInherentlyZeroData
                        ? 'default'
                        : 'pointer',
                  }}
                >
                  {item.label}
                  {selectedBarData &&
                    visibleKeys[gk.key] &&
                    selectedBarData[`widgets.${subtype}.${gk.key}`] != null && (
                      <strong style={{ marginLeft: '0.25em', fontWeight: 600 }}>
                        |{' '}
                        {formatToMaxFourDigits(
                          selectedBarData[`widgets.${subtype}.${gk.key}`],
                          kpiContext === 'Economics'
                        )}{' '}
                        {unit}
                      </strong>
                    )}
                </button>
              );
            } else if (
              item.type === 'group' &&
              item.memberConfigs &&
              item.memberConfigs.length > 0
            ) {
              const groupMemberKeys = item.memberConfigs.map((mc) => mc.key);
              const anyCombinedVisible = groupMemberKeys.some(
                (mk) => visibleKeys[mk]
              );

              return (
                <div
                  key={item.id}
                  onClick={() => {
                    if (isInherentlyZeroData || isNullData) return;

                    const newVisibilityState = !anyCombinedVisible;
                    if (!newVisibilityState) {
                      const visibleKeysOutsideThisGroup = seriesKeys.filter(
                        (k) => visibleKeys[k] && !groupMemberKeys.includes(k)
                      ).length;
                      const visibleKeysInThisGroup = groupMemberKeys.filter(
                        (k) => visibleKeys[k]
                      ).length;

                      if (
                        visibleKeysOutsideThisGroup === 0 &&
                        visibleKeysInThisGroup > 0 &&
                        groupMemberKeys.every((mk) => visibleKeys[mk])
                      ) {
                        const totalVisibleKeys = seriesKeys.filter(
                          (k) => visibleKeys[k]
                        ).length;
                        if (totalVisibleKeys === groupMemberKeys.length) {
                          return;
                        }
                      }
                    }

                    setVisibleKeys((prev) => {
                      const updated = { ...prev };
                      groupMemberKeys.forEach((mk) => {
                        updated[mk] = newVisibilityState;
                      });
                      return updated;
                    });
                  }}
                  className={`detail ${styles.Stackbar__Key}`}
                  style={{
                    display: 'inline-flex',
                    backgroundColor: 'transparent',
                    color: ThemeStore.colors.text_secondary || '#505558',
                    borderRadius: '1px',
                    opacity:
                      selectedDate &&
                      !anyCombinedVisible &&
                      Object.values(visibleKeys).some((v) => !v)
                        ? 0.5
                        : 1,
                    cursor:
                      isNullData || isInherentlyZeroData
                        ? 'default'
                        : 'pointer',
                    marginRight: '10px',
                  }}
                >
                  {item.memberConfigs.map((mc) => (
                    <React.Fragment key={mc.key}>
                      <span
                        style={{
                          padding: '0 0.25em',
                          borderBottom: `4px solid ${
                            anyCombinedVisible
                              ? colorScale(mc.key)
                              : axisLineColor
                          }`,
                        }}
                      >
                        {getGraphKeyLabel(mc.key)}
                      </span>
                      {selectedBarData &&
                        anyCombinedVisible &&
                        selectedBarData[`widgets.${subtype}.${mc.key}`] !=
                          null && (
                          <span
                            style={{
                              fontWeight: 600,
                              borderBottom: `4px solid ${
                                anyCombinedVisible
                                  ? colorScale(mc.key)
                                  : 'transparent'
                              }`,
                            }}
                          >
                            |{' '}
                            {formatToMaxFourDigits(
                              selectedBarData[`widgets.${subtype}.${mc.key}`],
                              kpiContext === 'Economics'
                            )}{' '}
                            {unit}
                          </span>
                        )}
                    </React.Fragment>
                  ))}
                </div>
              );
            }
            return null;
          })}
        </div>
      </div>
      <svg
        ref={svgRef}
        width={chartWidth - platformConfig.containerPadding}
        height={height - platformConfig.headerHeight}
        onMouseMove={handlePointerMove}
        onMouseLeave={handlePointerLeave}
        onTouchMove={handlePointerMove}
        onTouchEnd={cleanupTouchInteraction}
        onTouchCancel={cleanupTouchInteraction}
        onTouchStart={() => {
          if (isNullData || is0ValueData) return;
          triggerThrottledHaptic();
        }}
        style={{
          userSelect: 'none',
          cursor: isNullData || is0ValueData ? 'default' : 'pointer',
        }}
      >
        <AxisLeft
          left={margin.left}
          scale={yScale}
          numTicks={is0ValueData || isNullData ? 0 : 4}
          stroke={axisLineColor}
          hideTicks={true}
          tickFormat={(value) =>
            value.toLocaleString(getNumberFormatLocale(), {
              minimumFractionDigits: 0,
              maximumFractionDigits: 2,
            })
          }
          tickLabelProps={() => ({
            fill: axisTickLabelColor,
            fontSize: 12,
            textAnchor: 'end',
            dy: '0.33em',
            opacity: is0ValueData || isNullData ? 0 : 1,
            display: isLoading ? 'none' : 'block',
          })}
          top={margin.top + CHART_CONFIG.axis.leftTopOffset}
          labelProps={{
            fill: axisTickLabelColor,
            fontSize: 12,
            textAnchor: 'middle',
            x: isWeb ? -10 : -15, // Mobile: more to the left
            y: -10,
          }}
          label={unit}
          strokeWidth={1}
          tickClassName={`${styles.BarStack__Axis} detail`}
          labelClassName={`${styles['rotate']} ${styles['rotate__360']} detail`}
        />
        <AxisBottom
          top={yMax + margin.bottom + CHART_CONFIG.axis.bottomTopOffset}
          left={margin.left}
          scale={dateScale}
          numTicks={4}
          tickValues={finalTickValues}
          stroke={axisLineColor}
          hideTicks={true}
          tickLabelProps={(value, index, ticks) => {
            const isLastTick = index === ticks.length - 1;
            return {
              fill: axisTickLabelColor,
              fontSize: 12,
              textAnchor: isLastTick ? 'end' : 'middle',
              dx: isLastTick ? -5 : 0,
            };
          }}
          tickFormat={(value) => {
            const date = DateTime.fromJSDate(new Date(value as any));
            return formatDate(date.toISO() as string, selectedTimeSet);
          }}
          axisClassName={`${styles.BarStack__Axis} detail`}
        />
        {(chartWidth > 0 && isLoading) ||
        (chartWidth > 0 && isNullData) ||
        (chartWidth > 0 && is0ValueData) ? (
          <foreignObject
            x={0}
            y={0}
            width={chartWidth - platformConfig.containerPadding}
            height={height - platformConfig.headerHeight}
            style={{
              pointerEvents: 'none',
            }}
          >
            <div
              style={{
                height: '100%',
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                opacity: 0.9,
              }}
              className={styles.Stackbar__NoData}
            >
              <IconWrapper
                iconName={
                  isLoading
                    ? 'loading'
                    : is0ValueData
                    ? 'ellipse'
                    : 'no_data_chart'
                }
                style={{ marginBottom: 8 }}
                className={styles.Stackbar__NoData__Icon}
              />
              <div
                className={`${styles.Stackbar__NoData__Label} detail`}
                style={{ width: '70%' }}
              >
                {isLoading
                  ? t('charts.loading')
                  : is0ValueData
                  ? t('charts.everything_is_zero')
                  : t('charts.data_unavailable')}
              </div>
            </div>
          </foreignObject>
        ) : (
          <Group
            top={margin.top + CHART_CONFIG.chartPadding.groupOffset}
            left={margin.left}
          >
            <BarStack<any, string>
              data={processedData}
              keys={seriesKeys}
              x={getDate}
              xScale={dateScale}
              yScale={yScale}
              color={colorScale}
            >
              {(barStacks) =>
                barStacks.map((barStack) =>
                  barStack.bars.map((bar, barIndexInStack) => {
                    const barDataForDate = bar.bar.data;
                    const currentSeriesKey = bar.key;
                    const currentKeyConfig =
                      seriesKeyConfigs.get(currentSeriesKey);
                    const barDate = getDate(barDataForDate);

                    if (!currentKeyConfig || !visibleKeys[currentSeriesKey])
                      return null;

                    // Don't render bars with no data to avoid stroke artifacts
                    const dataValue = barDataForDate[currentSeriesKey];
                    if (!dataValue || dataValue <= 0 || bar.height <= 0)
                      return null;

                    const isSelectedDate = selectedDate === barDate;
                    const barFillColor =
                      selectedDate && !isSelectedDate
                        ? getGreyScaledColor(bar.color)
                        : bar.color;

                    let radiusTopLeft = false,
                      radiusTopRight = false,
                      radiusBottomLeft = false,
                      radiusBottomRight = false;

                    if (!currentKeyConfig.combined) {
                      radiusTopLeft = true;
                      radiusTopRight = true;
                      radiusBottomLeft = true;
                      radiusBottomRight = true;
                    } else {
                      const currentKeyIndexInSeries =
                        seriesKeys.indexOf(currentSeriesKey);
                      let isTopMostVisibleCombined = true;
                      for (
                        let i = currentKeyIndexInSeries + 1;
                        i < seriesKeys.length;
                        i++
                      ) {
                        const nextSeriesKey = seriesKeys[i];
                        const nextKeyConfig =
                          seriesKeyConfigs.get(nextSeriesKey);
                        if (
                          nextKeyConfig &&
                          nextKeyConfig.combined &&
                          visibleKeys[nextSeriesKey] &&
                          barDataForDate[nextSeriesKey] > 0
                        ) {
                          isTopMostVisibleCombined = false;
                          break;
                        }
                        if (nextKeyConfig && !nextKeyConfig.combined) {
                          break;
                        }
                      }
                      if (isTopMostVisibleCombined) {
                        radiusTopLeft = true;
                        radiusTopRight = true;
                      }

                      let isBottomMostVisibleCombined = true;
                      for (let i = currentKeyIndexInSeries - 1; i >= 0; i--) {
                        const prevSeriesKey = seriesKeys[i];
                        const prevKeyConfig =
                          seriesKeyConfigs.get(prevSeriesKey);
                        if (
                          prevKeyConfig &&
                          prevKeyConfig.combined &&
                          visibleKeys[prevSeriesKey] &&
                          barDataForDate[prevSeriesKey] > 0
                        ) {
                          isBottomMostVisibleCombined = false;
                          break;
                        }
                        if (prevKeyConfig && !prevKeyConfig.combined) {
                          break;
                        }
                      }
                      if (isBottomMostVisibleCombined) {
                        radiusBottomLeft = true;
                        radiusBottomRight = true;
                      }
                    }

                    const initialYPos = yMax;
                    const initialHeightVal = 0;

                    // Apply vertical gap between stacked bars
                    // Reduce height of non-bottom bars to create visual separation
                    // FIX: barIndexInStack represents the column index, NOT the stack position
                    // We need to check if this is the bottom bar by comparing the key position in seriesKeys
                    const currentKeyIndex = seriesKeys.indexOf(currentSeriesKey);
                    const isBottomBar = currentKeyIndex === 0;
                    const gapAdjustment = isBottomBar ? 0 : BAR_CONFIG.stackGap;
                    const targetYPos = bar.y; // Don't shift position, just reduce height
                    const targetHeightVal = Math.max(0, bar.height - gapAdjustment);

                    const barXToPass = currentKeyConfig.combined
                      ? bar.x + 0.5
                      : bar.x;
                    const barWidthToPass = currentKeyConfig.combined
                      ? Math.max(0, bar.width - 1)
                      : bar.width;

                    return (
                      <AnimatedBar
                        // Key includes timeSet so bars remount on view change (day→month→year)
                        // but NOT the date, so transitions are smooth when data updates
                        key={`animated-bar-${currentSeriesKey}-${bar.index}-${selectedTimeSet}`}
                        xPos={barXToPass}
                        initialY={initialYPos}
                        initialHeight={initialHeightVal}
                        targetY={targetYPos}
                        targetHeight={targetHeightVal}
                        width={barWidthToPass}
                        fill={barFillColor}
                        stroke={
                          currentKeyConfig.combined ? barFillColor : 'white'
                        }
                        strokeWidth={currentKeyConfig.combined ? 0 : 1}
                        radius={BAR_CONFIG.cornerRadius}
                        radiusTopLeft={radiusTopLeft}
                        radiusTopRight={radiusTopRight}
                        radiusBottomLeft={radiusBottomLeft}
                        radiusBottomRight={radiusBottomRight}
                        shouldAnimate={animate}
                        delay={barIndexInStack * 30}
                        springConfig={{ tension: 170, friction: 26 }}
                        isCombined={currentKeyConfig.combined}
                        barXForCombined={
                          currentKeyConfig.combined ? bar.x : undefined
                        }
                        barWidthForCombined={
                          currentKeyConfig.combined ? bar.width : undefined
                        }
                      />
                    );
                  })
                )
              }
            </BarStack>
          </Group>
        )}
      </svg>
    </div>
  );
};

export default StackbarFixedRadius;
